/**
 * Lightweight pub-sub for "the uploader auto-paused an upload due to a
 * transient error" (bug 2026-08 follow-up).
 *
 * Prior to the sentinel refactor (see autoPauseSentinel.js), the auto-pause
 * signal reached Redux via `packagesSlice.uploadPackage`'s catch block —
 * because runOne rejected with `code = 'AUTO_PAUSED_ON_ERROR'`. That
 * rejection propagated cleanly and the catch dispatched PAUSED.
 *
 * The sentinel refactor turned the rejection into a *resolution* so the
 * worker loop wouldn't die (Resume no longer worked without live workers).
 * That's the right transport-layer fix but it broke the Redux update path,
 * because a resolved runOne doesn't trigger the outer catch. This module
 * provides the side-channel: ChunkedUploadService fires here from within
 * onError, and store.js subscribes at boot to dispatch the PAUSED action.
 *
 * Kept as a plain module-level Set (not RxJS / event target) to match the
 * existing pattern in uploadCompletionEvents.js.
 */

const listeners = new Set();

/**
 * @param {{ uploadId: string, reason?: string, message?: string }} payload
 */
export const notifyAutoPause = (payload) => {
  if (!payload || !payload.uploadId) return;
  for (const listener of listeners) {
    try {
      listener(payload);
    } catch (err) {
      // Never let a subscriber's throw break the uploader.
      // eslint-disable-next-line no-console
      console.warn("[autoPauseEvents] listener threw", err?.message);
    }
  }
};

/**
 * @param {(payload: { uploadId: string, reason?: string, message?: string }) => void} listener
 * @returns {() => void} unsubscribe
 */
export const subscribeAutoPause = (listener) => {
  if (typeof listener !== "function") return () => {};
  listeners.add(listener);
  return () => listeners.delete(listener);
};

// Test-only reset.
export const __resetAutoPauseListenersForTests = () => listeners.clear();
